import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { deepMerge } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useOccDisplayText } from '../../../context/occ/hooks/use-occ-display-text';
import { getKeyCodeForEvent, KeyCode } from '../../../utils/key-code';
import { Button } from '../../button/button';
import { HelpTextList } from '../help-text-list/help-text-list';
import { FormSkeleton } from './subcomponents/skeleton';
import { FormContextInstance } from './types/context';
const b = bemClassBuilder('v-form');
const FormBase = (props, ref) => {
    var _a, _b;
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { config, disabled, events, displayText, validator, isLoading, setFormData, assets, helpTexts } = props;
    const { cancelButton, submitButton } = config;
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const { form: formDisplayText } = useOccDisplayText();
    useImperativeHandle(ref, () => ({
        attemptSubmit,
        setFormData: newFormData => setFormData(data => deepMerge(data, newFormData))
    }));
    const setFormSubmittingState = useCallback((isSubmitting) => {
        var _a;
        setIsFormSubmitting(isSubmitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, isSubmitting);
    }, [events]);
    const attemptSubmit = useCallback(async () => {
        const { onSubmit, onAfterSuccessfulSubmit, onAfterFailedSubmit, onBeforeSubmit } = events;
        try {
            onBeforeSubmit === null || onBeforeSubmit === void 0 ? void 0 : onBeforeSubmit();
            setFormSubmittingState(true);
            const validationResult = await validator();
            if (validationResult.isValid) {
                setSubmitFailed(false);
                await onSubmit();
                await (onAfterSuccessfulSubmit === null || onAfterSuccessfulSubmit === void 0 ? void 0 : onAfterSuccessfulSubmit());
                setFormSubmittingState(false);
                return true;
            }
        }
        catch (error) {
            setSubmitFailed(true);
            onAfterFailedSubmit === null || onAfterFailedSubmit === void 0 ? void 0 : onAfterFailedSubmit(error);
        }
        finally {
            setFormSubmittingState(false);
        }
        return false;
    }, [events, setFormSubmittingState, validator]);
    const handleKeyPress = useCallback(event => {
        const keyCode = getKeyCodeForEvent(event);
        if (keyCode === KeyCode.Enter) {
            event.preventDefault();
            attemptSubmit();
        }
    }, [attemptSubmit]);
    const handleSubmit = useCallback(event => {
        event.preventDefault();
        attemptSubmit();
    }, [attemptSubmit]);
    const handleCancel = useCallback(event => {
        var _a;
        event.preventDefault();
        (_a = events.onCancelButtonClicked) === null || _a === void 0 ? void 0 : _a.call(events);
    }, [events]);
    const showButtons = config.cancelButton.show || config.submitButton.show;
    if (isLoading) {
        return enableSkeletonLoadingIndicators ? (_jsx(FormSkeleton, { className: props.className, config: config, showButtons: showButtons, children: props.children })) : null;
    }
    return (_jsx(FormContextInstance.Provider, { value: { submitFailed }, children: _jsxs("form", { id: props.id, className: b.block(undefined, props.className), onKeyPress: handleKeyPress, onSubmit: handleSubmit, noValidate: true, children: [props.children, helpTexts && _jsx(HelpTextList, { helpTexts: helpTexts }), showButtons && (_jsxs("div", { className: b.element('buttons'), children: [cancelButton.show && (_jsx(Button, Object.assign({}, cancelButton, { disabled: disabled, label: (_a = displayText === null || displayText === void 0 ? void 0 : displayText.cancelButtonLabel) !== null && _a !== void 0 ? _a : formDisplayText.cancelButtonLabel, className: b.element('cancel-button'), onClick: handleCancel, iconName: assets === null || assets === void 0 ? void 0 : assets.cancelButtonIconName }))), submitButton.show && (_jsx(Button, Object.assign({}, submitButton, { disabled: disabled, label: (_b = displayText === null || displayText === void 0 ? void 0 : displayText.submitButtonLabel) !== null && _b !== void 0 ? _b : formDisplayText.submitButtonLabel, type: "submit", className: b.element('submit-button'), loading: isFormSubmitting, onClick: handleSubmit, loadingIconName: assets === null || assets === void 0 ? void 0 : assets.submitButtonLoadingIconName, iconName: assets === null || assets === void 0 ? void 0 : assets.submitButtonIconName })))] }))] }) }));
};
export const Form = observer(forwardRef(FormBase));
Form.displayName = 'Form';
//# sourceMappingURL=form.js.map